import {
  Box,
  Flex,
  Toaster as ChakraToaster,
  Portal,
  Spinner,
  Text,
  Toast,
  createToaster,
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { LuCircleCheck, LuCircleX, LuInfo, LuTriangleAlert, LuX } from 'react-icons/lu'
import { FONT_DISPLAY, INK, INK_MUTED, LINE, SHADOW_LG } from './tokens'

const _toaster = createToaster({
  placement: 'top-end',
  pauseOnPageIdle: true,
  max: 3,
})

type ToastOptions = Parameters<typeof _toaster.create>[0]

export const toaster = {
  success: (opts: ToastOptions) => _toaster.create({ title: 'Listo', type: 'success', duration: 4000, ...opts }),
  error: (opts: ToastOptions) => _toaster.create({ title: 'Algo salió mal', type: 'error', duration: 6000, ...opts }),
  // `warning` e `info` faltaban: un toaster.info() en el editor de componentes lanzaba
  // TypeError justo al registrar un componente fuera del polígono (aviso perdido y
  // formulario sin limpiar).
  warning: (opts: ToastOptions) => _toaster.create({ title: 'Atención', type: 'warning', duration: 5000, ...opts }),
  info: (opts: ToastOptions) => _toaster.create({ type: 'info', duration: 4000, ...opts }),
  create: (opts: ToastOptions) => _toaster.create(opts),
}

const KIND = {
  success: { color: '#2a8a87', soft: '#dcefed', Icon: LuCircleCheck },
  error: { color: '#c8442f', soft: '#fae1db', Icon: LuCircleX },
  warning: { color: '#a8740e', soft: '#f9eccf', Icon: LuTriangleAlert },
  info: { color: '#3d6fa8', soft: '#e2eaf4', Icon: LuInfo },
  loading: { color: '#3d6fa8', soft: '#e2eaf4', Icon: LuInfo },
} as const

/**
 * Toasts con identidad: el ícono "aterriza" con un resorte y una barra de vida
 * corre bajo el texto — dice cuánto le queda al aviso sin que tengas que adivinar.
 */
export const Toaster = () => {
  return (
    <Portal>
      <ChakraToaster toaster={_toaster} insetInline={{ mdDown: '4' }}>
        {(toast) => {
          const k = KIND[(toast.type as keyof typeof KIND) ?? 'info'] ?? KIND.info
          const duration = typeof toast.duration === 'number' && Number.isFinite(toast.duration) ? toast.duration : null
          return (
            <Toast.Root
              width={{ md: '380px' }}
              bg="white"
              color={INK}
              borderRadius="16px"
              border={`1px solid ${LINE}`}
              boxShadow={SHADOW_LG}
              p={0}
              overflow="hidden"
            >
              <Flex gap={3} align="flex-start" p={3.5} pr={10} w="100%">
                {toast.type === 'loading' ? (
                  <Flex w="34px" h="34px" borderRadius="11px" align="center" justify="center" bg={k.soft} flexShrink={0}>
                    <Spinner size="sm" color={k.color} />
                  </Flex>
                ) : (
                  <motion.div initial={{ scale: 0.3, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 500, damping: 18 }} style={{ flexShrink: 0 }}>
                    <Flex w="34px" h="34px" borderRadius="11px" align="center" justify="center" bg={k.soft} color={k.color}>
                      <k.Icon size={18} />
                    </Flex>
                  </motion.div>
                )}
                <Box flex="1" minW={0} pt="1px">
                  {toast.title && (
                    <Toast.Title asChild>
                      <Text fontFamily={FONT_DISPLAY} fontWeight={700} fontSize="14.5px" letterSpacing="-0.01em">{toast.title as string}</Text>
                    </Toast.Title>
                  )}
                  {toast.description && (
                    <Toast.Description asChild>
                      <Text fontSize="13.5px" color={INK_MUTED} mt={0.5} lineHeight="1.45">{toast.description as string}</Text>
                    </Toast.Description>
                  )}
                  {toast.action && (
                    <Toast.ActionTrigger mt={2} fontSize="13px" fontWeight={600} color={k.color}>{toast.action.label}</Toast.ActionTrigger>
                  )}
                </Box>
              </Flex>
              <Toast.CloseTrigger position="absolute" top="10px" right="10px" p={1.5} borderRadius="8px" color={INK_MUTED} _hover={{ bg: '#f3f1ed', color: INK }}>
                <LuX size={15} />
              </Toast.CloseTrigger>
              {duration && toast.type !== 'loading' && (
                <motion.div
                  initial={{ scaleX: 1 }}
                  animate={{ scaleX: 0 }}
                  transition={{ duration: duration / 1000, ease: 'linear' }}
                  style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3, background: k.color, transformOrigin: 'left', opacity: 0.85 }}
                />
              )}
            </Toast.Root>
          )
        }}
      </ChakraToaster>
    </Portal>
  )
}
